import { queryOptions } from "@tanstack/react-query";
import type { DayCalculationRequest, YearCalculationRequest } from "../types/api";
import { api } from "./api";
import { cacheNeedsRefresh, getYearDataCache, setYearDataCache } from "./yearCache";

const DAY_OPTIONS = {
	timezone: "Europe/Oslo",
	distance: 25000,
	anglePrecision: 1,
	timePrecision: 60,
};

export function dayQueryOptions(input: { lat: number; lon: number; date: string }) {
	const request: DayCalculationRequest = {
		date: input.date,
		location: { latitude: input.lat, longitude: input.lon },
		options: DAY_OPTIONS,
	};
	return queryOptions({
		queryKey: ["day", input.lat, input.lon, input.date],
		queryFn: () => api.calculateDay(request),
	});
}

export function yearQueryOptions(input: { lat: number; lon: number; year: number }) {
	const request: YearCalculationRequest = {
		year: input.year,
		location: { latitude: input.lat, longitude: input.lon },
		options: DAY_OPTIONS,
	};
	return queryOptions({
		queryKey: ["year", input.lat, input.lon, input.year],
		queryFn: async () => {
			// The cache saves recomputing a year, but it lives in the browser: only
			// trust it once the server confirms this browser may open the place
			// (logged-in owner or guest place pass). Otherwise ask for the year, so a
			// logged-out or different visitor gets the server's real answer.
			const cached = getYearDataCache(input.lat, input.lon, input.year);
			if (cached && !cacheNeedsRefresh(cached)) {
				const { hasAccess } = await api
					.getAccess({ latitude: input.lat, longitude: input.lon })
					.catch(() => ({ hasAccess: false }));
				if (hasAccess) return cached;
			}
			const response = await api.calculateYear(request);
			setYearDataCache(input.lat, input.lon, input.year, response);
			return response;
		},
	});
}

/** Horizon profile and sun path for one day — the data behind the horizon scene. */
export function terrainSunQueryOptions(input: { lat: number; lon: number; date: string }) {
	return queryOptions({
		queryKey: ["terrain-sun", input.lat, input.lon, input.date],
		queryFn: () =>
			api.getTerrainSun({
				date: input.date,
				location: { latitude: input.lat, longitude: input.lon },
				options: { timezone: DAY_OPTIONS.timezone },
			}),
		staleTime: Number.POSITIVE_INFINITY,
	});
}
